import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

// Supported countries with their dial codes and formats
const COUNTRIES = [
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', dialCode: '+852', format: '#### ####', maxLength: 8, timezones: ['Asia/Hong_Kong'] },
  { code: 'MO', name: 'Macau', flag: '🇲🇴', dialCode: '+853', format: '#### ####', maxLength: 8, timezones: ['Asia/Macau'] },
  { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86', format: '### #### ####', maxLength: 11, timezones: ['Asia/Shanghai', 'Asia/Chongqing', 'Asia/Harbin', 'Asia/Urumqi', 'Asia/Kashgar'] },
  { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1', format: '(###) ###-####', maxLength: 10, timezones: ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'America/Honolulu', 'America/Detroit', 'America/Indiana/Indianapolis', 'America/Kentucky/Louisville', 'America/Boise'] },
  { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1', format: '(###) ###-####', maxLength: 10, timezones: ['America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 'America/Montreal', 'America/Regina'] },
];

// Detect country from browser timezone
const detectCountryFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Find exact match first
    for (const country of COUNTRIES) {
      if (country.timezones.includes(timezone)) {
        return country.code;
      }
    }
    
    // Fallback: check if timezone starts with region
    if (timezone.startsWith('Asia/Hong')) return 'HK';
    if (timezone.startsWith('Asia/Macau') || timezone.startsWith('Asia/Macao')) return 'MO';
    if (timezone.startsWith('Asia/')) return 'CN'; // Default to China for other Asian timezones
    if (timezone.startsWith('America/')) {
      // Try to distinguish US vs Canada based on common timezones
      const canadianTimezones = ['Toronto', 'Vancouver', 'Edmonton', 'Winnipeg', 'Halifax', 'St_Johns', 'Montreal', 'Regina'];
      const city = timezone.split('/')[1];
      if (canadianTimezones.some(c => city?.includes(c))) return 'CA';
      return 'US';
    }
    
    // Default to Hong Kong
    return 'HK';
  } catch {
    return 'HK';
  }
};

// Format phone number based on country format pattern
const formatPhoneNumber = (value, format) => {
  if (!value) return '';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  let result = '';
  let digitIndex = 0;
  
  for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
    if (format[i] === '#') {
      result += digits[digitIndex];
      digitIndex++;
    } else {
      result += format[i];
    }
  }
  
  return result;
};

// Parse phone number to extract country and local number
const parsePhoneNumber = (fullNumber) => {
  if (!fullNumber) return { countryCode: null, localNumber: '' };
  
  const cleaned = fullNumber.replace(/\D/g, '');
  
  // Try to match country dial codes (longest first)
  for (const country of [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length)) {
    const dialDigits = country.dialCode.replace(/\D/g, '');
    if (cleaned.startsWith(dialDigits)) {
      return {
        countryCode: country.code,
        localNumber: cleaned.slice(dialDigits.length)
      };
    }
  }
  
  return { countryCode: null, localNumber: cleaned };
};

const PhoneInput = ({ 
  value = '', 
  onChange, 
  placeholder = 'Phone number',
  className = '',
  disabled = false,
  required = false,
  id,
  name,
  'data-testid': dataTestId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(() => {
    // Initialize country from value or auto-detect
    if (value) {
      const { countryCode } = parsePhoneNumber(value);
      if (countryCode) {
        const country = COUNTRIES.find(c => c.code === countryCode);
        if (country) return country;
      }
    }
    // Auto-detect from timezone
    const detectedCode = detectCountryFromTimezone();
    return COUNTRIES.find(c => c.code === detectedCode) || COUNTRIES[0];
  });
  const [localNumber, setLocalNumber] = useState(() => {
    // Initialize local number from value
    if (value) {
      const { countryCode, localNumber: parsed } = parsePhoneNumber(value);
      if (countryCode) return parsed;
      // If value doesn't have country code, use it as is
      if (!value.startsWith('+')) return value.replace(/\D/g, '');
    }
    return '';
  });
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    
    // Reformat and emit the full number
    const fullNumber = localNumber ? `${country.dialCode}${localNumber}` : '';
    onChange?.(fullNumber);
    
    // Focus the input after selection
    inputRef.current?.focus();
  };

  // Handle phone input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Extract only digits
    const digits = inputValue.replace(/\D/g, '');
    
    // Limit to max length for country
    const limitedDigits = digits.slice(0, selectedCountry?.maxLength || 15);
    
    setLocalNumber(limitedDigits);
    
    // Emit full international format
    const fullNumber = limitedDigits ? `${selectedCountry?.dialCode || ''}${limitedDigits}` : '';
    onChange?.(fullNumber);
  };

  // Get formatted display value
  const displayValue = selectedCountry ? formatPhoneNumber(localNumber, selectedCountry.format) : localNumber;

  return (
    <div className={`flex ${className}`} ref={dropdownRef}>
      {/* Country Selector */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex items-center gap-1 px-3 py-2 border border-r-0 rounded-l-md bg-gray-50 hover:bg-gray-100 transition-colors min-w-[100px] justify-between ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          data-testid={dataTestId ? `${dataTestId}-country-selector` : undefined}
        >
          <span className="flex items-center gap-1">
            <span className="text-lg">{selectedCountry?.flag}</span>
            <span className="text-sm text-gray-600">{selectedCountry?.dialCode}</span>
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {/* Dropdown */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountrySelect(country)}
                className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 transition-colors text-left ${selectedCountry?.code === country.code ? 'bg-yellow-50' : ''}`}
              >
                <span className="text-lg">{country.flag}</span>
                <span className="flex-1 text-sm">{country.name}</span>
                <span className="text-sm text-gray-500">{country.dialCode}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Phone Number Input */}
      <input
        ref={inputRef}
        type="tel"
        id={id}
        name={name}
        value={displayValue}
        onChange={handleInputChange}
        placeholder={selectedCountry ? selectedCountry.format.replace(/#/g, '0') : placeholder}
        disabled={disabled}
        required={required}
        className={`flex-1 px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        data-testid={dataTestId}
      />
    </div>
  );
};

// Helper function to format a stored phone number for display
export const formatStoredPhone = (phone) => {
  if (!phone) return '';
  const { countryCode, localNumber } = parsePhoneNumber(phone);
  if (countryCode) {
    const country = COUNTRIES.find(c => c.code === countryCode);
    if (country) {
      return `${country.dialCode} ${formatPhoneNumber(localNumber, country.format)}`;
    }
  }
  return phone;
};

// Helper to check if phone is valid
export const isValidPhone = (phone) => {
  if (!phone) return true; // Empty is valid (optional field)
  const { countryCode, localNumber } = parsePhoneNumber(phone);
  if (!countryCode) return false;
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) return false;
  return localNumber.length === country.maxLength;
};

export { COUNTRIES, detectCountryFromTimezone, parsePhoneNumber };
export default PhoneInput;
